import React, { ReactElement } from 'react';
import {
  Disclosure as AriaDisclosure,
  type DisclosureProps as AriaDisclosureProps,
  Button as AriaButton,
  DisclosurePanel as AriaDisclosurePanel,
  DisclosureGroup as AriaDisclosureGroup,
} from 'react-aria-components';
import { Icon } from '../Icon/Icon';
import styles from './Accordion.module.css';

type HeadingLevel = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface AccordionProps extends Omit<AriaDisclosureProps, 'children'> {
  /** The title text displayed in the accordion header */
  title: string;

  /** Optional description text displayed below the title */
  description?: string;

  /** Content rendered inside the accordion when expanded */
  children: React.ReactNode;

  /** Controls if accordion is expanded on initial render */
  defaultExpanded?: boolean;

  /** Additional CSS class for custom styling */
  className?: string;

  /**
   * Content (typically an icon) rendered before the heading.
   * **Deprecated. Use `Accordion.StartSlot` instead**
   * @deprecated
   */
  titleStart?: React.ReactNode;

  /**
   * Content (typically an icon) rendered after the heading.
   * **Deprecated. Use `Accordion.EndSlot` instead**
   * @deprecated
   */
  titleEnd?: React.ReactNode;

  /** Unique identifier for the accordion */
  id?: string;

  /** The semantic heading level for the accordion title */
  headingLevel?: HeadingLevel;

  /** Whether the accordion is disabled and cannot be interacted with */
  isDisabled?: boolean;
}

interface AccordionTriggerProps
  extends Pick<
    AccordionProps,
    'title' | 'description' | 'id' | 'headingLevel'
  > {
  startSlot?: React.ReactNode;
  endSlot?: React.ReactNode;
}

const AccordionTrigger = ({
  title,
  description,
  startSlot,
  endSlot,
  id,
  headingLevel = 'h3',
}: AccordionTriggerProps) => {
  const HeadingElement = headingLevel;

  return (
    <AriaButton
      slot="trigger"
      className={styles.accordion__trigger}
      {...(id ? { id } : {})}
    >
      <span className={styles['accordion__button-content']}>
        <span className={styles['accordion__title-container']}>
          {startSlot && (
            <span className={styles['accordion__start-slot']}>{startSlot}</span>
          )}
          <div className={styles['accordion__title-wrapper']}>
            <HeadingElement className={styles.accordion__title}>
              {title}
            </HeadingElement>
            {description && (
              <p className={styles.accordion__description}>{description}</p>
            )}
          </div>
        </span>
        <span className={styles['accordion__end-container']}>
          {endSlot && (
            <span className={styles['accordion__end-slot']}>{endSlot}</span>
          )}
          <Icon
            iconName="chevron-down"
            iconPrefix="fas"
            className={styles['accordion__icon-wrapper']}
            color="accentSecondary"
          />
        </span>
      </span>
    </AriaButton>
  );
};

const AccordionPanel = ({
  children,
  ...props
}: React.ComponentProps<typeof AriaDisclosurePanel>) => {
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    // Get the parent group element
    const group = content.closest('[role="group"]') as HTMLElement;
    if (!group) return;

    requestAnimationFrame(() => {
      const height = content.scrollHeight;
      group.style.setProperty('--group-height', `${height}px`);
    });

    const updateHeight = () => {
      const height = content.scrollHeight;
      group.style.setProperty('--group-height', `${height}px`);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    resizeObserver.observe(content);
    resizeObserver.observe(group);

    window.addEventListener('resize', updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <AriaDisclosurePanel {...props}>
      <div ref={contentRef} className={styles['accordion__panel-content']}>
        {children}
      </div>
    </AriaDisclosurePanel>
  );
};

interface SlotProps {
  children: React.ReactNode;
}

export const StartSlot = ({ children }: SlotProps) => <>{children}</>;
export const EndSlot = ({ children }: SlotProps) => <>{children}</>;

export const Accordion = ({
  title,
  description,
  children,
  defaultExpanded = false,
  className,
  titleStart,
  titleEnd,
  id,
  headingLevel = 'h3',
  ...props
}: AccordionProps) => {
  const accordionClasses = [styles.accordion, className]
    .filter(Boolean)
    .join(' ');

  // Pick start and end content out of `children`
  let startSlot = titleStart;
  let endSlot = titleEnd;
  const panelContent = React.Children.map(children, (child) => {
    const childElement = child as ReactElement<SlotProps>;

    if (childElement.type === StartSlot) {
      startSlot = childElement.props.children;
      return null;
    }

    if (childElement.type === EndSlot) {
      endSlot = childElement.props.children;
      return null;
    }

    return childElement;
  })?.filter(Boolean);

  return (
    <AriaDisclosure
      className={accordionClasses}
      defaultExpanded={defaultExpanded}
      id={id}
      {...props}
    >
      <AccordionTrigger
        title={title}
        description={description}
        startSlot={startSlot}
        endSlot={endSlot}
        id={id}
        headingLevel={headingLevel}
      />
      <AccordionPanel>{panelContent}</AccordionPanel>
    </AriaDisclosure>
  );
};

export interface AccordionGroupProps {
  /** Title for the accordion group - can be string or ReactNode */
  title?: string | React.ReactNode;

  /** Description for the accordion group - can be string or ReactNode */
  description?: string | React.ReactNode;

  /** Collection of Accordion components */
  children?: React.ReactNode;

  /** Additional CSS class for custom styling */
  className?: string;

  /** Whether multiple accordions can be expanded at once */
  allowsMultipleExpanded?: boolean;
}

export const AccordionGroup = ({
  title,
  description,
  children,
  className,
  allowsMultipleExpanded = true,
}: AccordionGroupProps) => {
  const groupClasses = [styles['accordion-group'], className]
    .filter(Boolean)
    .join(' ');

  // Find any children that have defaultExpanded={true}
  const defaultExpandedKeys = React.Children.toArray(children)
    .map((child, index) => {
      if (
        React.isValidElement(child) &&
        typeof child.props === 'object' &&
        child.props !== null &&
        'defaultExpanded' in child.props &&
        child.props.defaultExpanded
      ) {
        return 'id' in child.props && typeof child.props.id === 'string'
          ? child.props.id
          : `accordion-${index}`;
      }
      return null;
    })
    .filter((key): key is string => key !== null);

  return (
    <div className={groupClasses}>
      {(title || description) && (
        <div className={styles['accordion-group__header']}>
          {title}
          {description}
        </div>
      )}
      <AriaDisclosureGroup
        className={styles['accordion-group__stack']}
        allowsMultipleExpanded={allowsMultipleExpanded}
        defaultExpandedKeys={defaultExpandedKeys}
      >
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement<AccordionProps>(child)) {
            return React.cloneElement(child, {
              key: `accordion-${index}`,
              id: child.props?.id || `accordion-${index}`,
            } as Partial<AccordionProps>);
          }
          return child;
        })}
      </AriaDisclosureGroup>
    </div>
  );
};

Accordion.StartSlot = StartSlot;
Accordion.EndSlot = EndSlot;
StartSlot.displayName = 'Accordion.StartSlot';
EndSlot.displayName = 'Accordion.EndSlot';

export default Accordion;
